import { V1BEClient } from "@/clients";
import { SignedUrl } from "@/types/FileTypes";
import {
  IEventIntProperty,
  IEventStringProperty,
  IUserOrganizationEventAttendee,
} from "@/types/EventTypes";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const isProd = () => {
  return (
    window.location.hostname.includes("master") &&
    !window.location.hostname.includes("dev")
  );
};

export const isDev = () => {
  return window.location.hostname.includes("amplifyapp.com");
};

export const isLocalhost = () => {
  return (
    window.location.hostname.includes("localhost") ||
    window.location.hostname.includes("127.0.0.1") ||
    window.location.hostname.includes("daz-eventhub.com")
  );
};

export const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export const formatDateRange = (from?: string, to?: string) => {
  if (!from && !to) return "TBA";
  const fromStr = from
    ? new Date(from).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";
  const toStr = to
    ? new Date(to).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";
  if (fromStr && toStr) return `${fromStr} - ${toStr}`;
  return fromStr || toStr;
};

export const toAcronym = (str: string) => {
  return str
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase();
};

export const getImageOrAcronym = (name: string, imageUrl?: string | null) => {
  const trimmed = (imageUrl || "").trim();
  if (trimmed.length > 0) {
    return { type: "image" as const, src: trimmed };
  }
  return { type: "acronym" as const, text: toAcronym(name || "") };
};

// Convert event_date to YYYY-MM-DD format for date input
export const formatDateForInput = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const countSupplierAttendees = (
  organization_event_attendees: IUserOrganizationEventAttendee[]
) => {
  return (
    organization_event_attendees?.filter((a) => a.attending_as === "supplier")
      .length ?? 0
  );
};

export const countSuppliers = (
  organization_event_attendees: IUserOrganizationEventAttendee[]
) => {
  const length = countSupplierAttendees(organization_event_attendees);
  return `${length} Supplier${length === 1 ? "" : "s"}`;
};

export const countGuestAttendees = (
  organization_event_attendees: IUserOrganizationEventAttendee[]
) => {
  return (
    organization_event_attendees?.filter((a) => a.attending_as === "guest")
      .length ?? 0
  );
};

export const countGuests = (
  organization_event_attendees: IUserOrganizationEventAttendee[]
) => {
  const length = countGuestAttendees(organization_event_attendees);
  return `${length} Guest${length === 1 ? "" : "s"}`;
};

export const getExpectedSupplierAttendees = (
  organization_event_int_properties: IEventIntProperty[]
) => {
  return (
    organization_event_int_properties?.find(
      (p) => p.name === "expected_supplier_attendance"
    )?.value ?? 0
  );
};

export const getExpectedGuestAttendees = (
  organization_event_int_properties: IEventIntProperty[]
) => {
  return (
    organization_event_int_properties?.find(
      (p) => p.name === "expected_guest_attendance"
    )?.value ?? 0
  );
};

export const countSuppliersOverExpected = (
  organization_event_attendees: IUserOrganizationEventAttendee[],
  organization_event_int_properties: IEventIntProperty[]
) => {
  const expectedSuppliers = getExpectedSupplierAttendees(
    organization_event_int_properties
  );
  const actualSuppliers = countSupplierAttendees(organization_event_attendees);
  return actualSuppliers + "/" + expectedSuppliers + " Suppliers";
};

export const countGuestsOverExpected = (
  organization_event_attendees: IUserOrganizationEventAttendee[],
  organization_event_int_properties: IEventIntProperty[]
) => {
  const expectedGuests = getExpectedGuestAttendees(
    organization_event_int_properties
  );
  const actualGuests = countGuestAttendees(organization_event_attendees);
  return actualGuests + "/" + expectedGuests + " Guests";
};

export const getLocation = (
  organization_event_string_properties: IEventStringProperty[]
) => {
  const loc =
    organization_event_string_properties?.find((p) => p.name === "location")
      ?.value || "TBA";

  return capitalize(loc);
};
